import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import {
  Alert, Autocomplete, Box, Button, Chip, CircularProgress, Dialog,
  DialogContent, DialogTitle, Divider, Grid, IconButton, MenuItem,
  Paper, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import SearchIcon        from '@mui/icons-material/Search';
import DownloadIcon      from '@mui/icons-material/Download';
import PictureAsPdfIcon  from '@mui/icons-material/PictureAsPdf';
import ManageSearchIcon  from '@mui/icons-material/ManageSearch';
import CloseIcon         from '@mui/icons-material/Close';
import ReceiptLongIcon   from '@mui/icons-material/ReceiptLong';
import PersonSearchIcon  from '@mui/icons-material/PersonSearch';
import { exportMultiSheetXlsx } from './excelExportUtils';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

const API = '/api/contabilidad';

function fmt(v) {
  return parseFloat(v || 0).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function fmtMes(p) {
  if (!p || p.length !== 6) return p || '';
  const mn = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];
  return `${mn[parseInt(p.substring(4,6),10)-1]} ${p.substring(0,4)}`;
}
function fmtFecha(d) {
  if (!d) return '';
  const s = typeof d === 'string' ? d.substring(0, 10) : String(d).substring(0, 10);
  const [y, m, day] = s.split('-');
  return (y && m && day) ? `${day}/${m}/${y}` : s;
}

// ── Colores por tipo de asiento ────────────────────────────────────────────────
const TIPO_COLOR = {
  APERTURA:   '#6a1b9a',
  DIARIO:     '#1565c0',
  CIERRE:     '#e65100',
  AJUSTE:     '#00695c',
  PROVISION:  '#558b2f',
  DEFAULT:    '#546e7a',
};
function tipoChipColor(tipo) { return TIPO_COLOR[tipo] || TIPO_COLOR.DEFAULT; }

// ── Popup: Detalle de Asiento ─────────────────────────────────────────────────
function AsientoDialog({ movcabId, onClose }) {
  const [loading, setLoading] = useState(true);
  const [data, setData]       = useState(null);
  const [error, setError]     = useState('');

  useEffect(() => {
    if (!movcabId) return;
    setLoading(true); setError(''); setData(null);
    axios.get(`${API}/asiento-detalle/${movcabId}`)
      .then(r => setData(r.data))
      .catch(e => setError(e.response?.data?.error || 'Error al cargar asiento'))
      .finally(() => setLoading(false));
  }, [movcabId]);

  const cab = data?.cabecera;

  return (
    <Dialog open={!!movcabId} onClose={onClose} maxWidth="lg" fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ pb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
        <ReceiptLongIcon color="primary" />
        <Box flex={1}>
          <Typography fontWeight="bold" fontSize={16}>Detalle de Asiento Contable</Typography>
          {cab && (
            <Typography variant="caption" color="text.secondary">
              {fmtMes(cab.periodo)} &nbsp;·&nbsp; {fmtFecha(cab.fecha)} &nbsp;·&nbsp; {cab.glosa}
            </Typography>
          )}
        </Box>
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ pt: 2 }}>
        {loading && <CircularProgress sx={{ display: 'block', mx: 'auto', my: 3 }} />}
        {error   && <Alert severity="error">{error}</Alert>}
        {data && (
          <>
            {/* Cabecera del asiento */}
            <Paper variant="outlined" sx={{ p: 1.5, mb: 2, borderRadius: 2, bgcolor: '#f8f9fa' }}>
              <Grid container spacing={1.5}>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">Glosa</Typography>
                  <Typography fontSize={13}>{cab.glosa || '—'}</Typography>
                </Grid>
                {[
                  { label: 'Moneda',     value: cab.moneda || 'PEN' },
                  { label: 'T. Cambio',  value: cab.moneda === 'USD' ? fmt(cab.tipo_cambio) : '—' },
                  { label: 'Debe',       value: fmt(data.total_debe),  color: '#1565c0' },
                  { label: 'Haber',      value: fmt(data.total_haber), color: '#c62828' },
                ].map(({ label, value, color }) => (
                  <Grid item key={label}>
                    <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
                    <Typography fontWeight="bold" fontSize={13} sx={color ? { color } : {}}>
                      {value}
                    </Typography>
                  </Grid>
                ))}
                {[
                  { label: 'N° Asiento', value: cab.numero, mono: true },
                  { label: 'Tipo',       value: cab.tipo   || '—', chip: true },
                  { label: 'Período',    value: fmtMes(cab.periodo) },
                  { label: 'Fecha',      value: fmtFecha(cab.fecha) },
                  { label: 'Estado',     value: cab.estado || '—' },
                ].map(({ label, value, mono, chip }) => (
                  <Grid item key={label}>
                    <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
                    {chip
                      ? <Chip label={value} size="small"
                          sx={{ bgcolor: tipoChipColor(value), color: '#fff', fontWeight: 'bold', fontSize: '0.7rem' }} />
                      : <Typography fontWeight="bold" fontSize={13}
                          sx={mono ? { fontFamily: 'monospace', color: '#1976d2' } : {}}>
                          {value}
                        </Typography>
                    }
                  </Grid>
                ))}
              </Grid>
            </Paper>

            {/* Líneas del asiento */}
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#e3f2fd' }}>
                    {['#','Cuenta','Auxiliar (RUC)','Razón Social','Glosa línea','Debe','Haber'].map(h => (
                      <TableCell key={h} sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}
                        align={['Debe','Haber'].includes(h) ? 'right' : 'left'}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.lineas.map((l, i) => (
                    <TableRow key={i} hover sx={{ '&:nth-of-type(odd)': { bgcolor: '#fafafa' } }}>
                      <TableCell sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>{l.linea}</TableCell>
                      <TableCell>
                        <Typography sx={{ fontFamily: 'monospace', color: '#1565c0', fontSize: '0.78rem' }}>
                          {l.cuenta_codigo}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {l.cuenta_descripcion}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.78rem', fontFamily: 'monospace', color: '#555' }}>
                        {l.auxiliar_ruc || '—'}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.78rem' }}>
                        {l.auxiliar_razon_social || l.auxiliar_nombre || '—'}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>
                        {l.glosa_det || ''}
                      </TableCell>
                      <TableCell align="right" sx={{ color: parseFloat(l.debe) > 0 ? '#1565c0' : 'text.disabled', fontWeight: parseFloat(l.debe) > 0 ? 'bold' : 'normal', fontSize: '0.82rem' }}>
                        {parseFloat(l.debe) > 0 ? fmt(l.debe) : '—'}
                      </TableCell>
                      <TableCell align="right" sx={{ color: parseFloat(l.haber) > 0 ? '#c62828' : 'text.disabled', fontWeight: parseFloat(l.haber) > 0 ? 'bold' : 'normal', fontSize: '0.82rem' }}>
                        {parseFloat(l.haber) > 0 ? fmt(l.haber) : '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                  {/* Totales */}
                  <TableRow sx={{ bgcolor: '#e8f0fe' }}>
                    <TableCell colSpan={5} sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>TOTALES</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', color: '#1565c0' }}>
                      {fmt(data.total_debe)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', color: '#c62828' }}>
                      {fmt(data.total_haber)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>

            {/* Indicador de cuadre */}
            {Math.abs(data.total_debe - data.total_haber) > 0.005 ? (
              <Alert severity="warning" sx={{ mt: 1.5 }}>
                Asiento desbalanceado: diferencia de S/ {fmt(Math.abs(data.total_debe - data.total_haber))}
              </Alert>
            ) : (
              <Alert severity="success" sx={{ mt: 1.5, py: 0.5 }} icon={false}>
                <Typography variant="caption">Asiento cuadrado ✓</Typography>
              </Alert>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ── Componente principal ───────────────────────────────────────────────────────
export default function AnalisisCuenta() {
  const [empresas, setEmpresas]         = useState([]);
  const [empresaId, setEmpresaId]       = useState('');
  const [empresa, setEmpresa]           = useState(null);
  const [periodos, setPeriodos]         = useState([]);

  // Filtros de período/fecha
  const [modoFecha, setModoFecha]       = useState(false); // false=período, true=fecha exacta
  const [periodoDesde, setPeriodoDesde] = useState('');
  const [periodoHasta, setPeriodoHasta] = useState('');
  const [fechaDesde, setFechaDesde]     = useState('');
  const [fechaHasta, setFechaHasta]     = useState('');

  // Filtro cuenta
  const [cuentaOpts, setCuentaOpts]     = useState([]);
  const [cuentaSel, setCuentaSel]       = useState(null);
  const [inputCuenta, setInputCuenta]   = useState('');
  const [buscandoCuenta, setBuscandoCuenta] = useState(false);

  // Filtro auxiliar
  const [auxOpts, setAuxOpts]           = useState([]);
  const [auxSel, setAuxSel]             = useState(null);
  const [inputAux, setInputAux]         = useState('');
  const [buscandoAux, setBuscandoAux]   = useState(false);

  // Resultados
  const [data, setData]                 = useState(null);
  const [loading, setLoading]           = useState(false);
  const [error, setError]               = useState('');

  // Popup asiento
  const [asientoId, setAsientoId]       = useState(null);

  // Fila resaltada por doble clic
  const lastClick = useRef({});

  useEffect(() => {
    axios.get(`${API}/empresas`).then(r => setEmpresas(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!empresaId) return;
    setEmpresa(empresas.find(e => e.id == empresaId) || null);
    setData(null); setCuentaSel(null); setInputCuenta('');
    setAuxSel(null); setInputAux(''); setAuxOpts([]);
    axios.get(`${API}/periodos?empresa_id=${empresaId}`).then(r => {
      setPeriodos(r.data);
      const ab = r.data.filter(p => p.estado === 'ABIERTO').sort((a,b) => a.periodo.localeCompare(b.periodo));
      if (ab.length > 0) {
        setPeriodoDesde(ab[0].periodo);
        setPeriodoHasta(ab[ab.length-1].periodo);
      }
    }).catch(() => {});
  }, [empresaId, empresas]);

  // Buscar cuentas
  const buscarCuentas = useCallback(async (term) => {
    if (!empresaId || !term) { setCuentaOpts([]); return; }
    setBuscandoCuenta(true);
    try {
      const r = await axios.get(`${API}/plan?empresa_id=${empresaId}&buscar=${encodeURIComponent(term)}&nivel_max=6`);
      setCuentaOpts(Array.isArray(r.data) ? r.data.slice(0, 30) : []);
    } catch (_) {}
    setBuscandoCuenta(false);
  }, [empresaId]);

  useEffect(() => {
    const t = setTimeout(() => buscarCuentas(inputCuenta), 280);
    return () => clearTimeout(t);
  }, [inputCuenta, buscarCuentas]);

  // Buscar auxiliares (solo cuando hay cuenta seleccionada)
  const buscarAuxiliares = useCallback(async (term) => {
    if (!empresaId) { setAuxOpts([]); return; }
    setBuscandoAux(true);
    try {
      const params = new URLSearchParams({ empresa_id: empresaId });
      if (cuentaSel?.id) params.append('cuenta_id', cuentaSel.id);
      if (term)          params.append('buscar', term);
      const r = await axios.get(`${API}/analisis-cuenta/auxiliares?${params}`);
      setAuxOpts(Array.isArray(r.data) ? r.data : []);
    } catch (_) {}
    setBuscandoAux(false);
  }, [empresaId, cuentaSel]);

  useEffect(() => {
    const t = setTimeout(() => buscarAuxiliares(inputAux), 280);
    return () => clearTimeout(t);
  }, [inputAux, buscarAuxiliares]);

  // Al seleccionar cuenta, precarga auxiliares
  useEffect(() => {
    if (cuentaSel) buscarAuxiliares('');
    else { setAuxSel(null); setAuxOpts([]); }
  }, [cuentaSel, buscarAuxiliares]);

  async function analizar() {
    if (!empresaId || !cuentaSel) { setError('Selecciona empresa y cuenta'); return; }
    setLoading(true); setError('');
    try {
      const params = new URLSearchParams({ empresa_id: empresaId, cuenta_id: cuentaSel.id });
      if (modoFecha) {
        if (fechaDesde) params.append('fecha_desde', fechaDesde);
        if (fechaHasta) params.append('fecha_hasta', fechaHasta);
      } else {
        if (periodoDesde) params.append('periodo_desde', periodoDesde);
        if (periodoHasta) params.append('periodo_hasta', periodoHasta);
      }
      if (auxSel) params.append('auxiliar_id', auxSel.id);
      const r = await axios.get(`${API}/analisis-cuenta?${params}`);
      setData(r.data);
    } catch (e) { setError(e.response?.data?.error || 'Error al analizar'); }
    setLoading(false);
  }

  // ── Determinar si la cuenta usa auxiliar ──────────────────────────────────
  const cuentaUsaAuxiliar = !!(data?.cuenta?.requiere_auxiliar || cuentaSel?.requiere_auxiliar);

  // ── Fila con doble clic ───────────────────────────────────────────────────
  function handleRowClick(e, movcabId) {
    const now = Date.now();
    if (lastClick.current.id === movcabId && now - lastClick.current.ts < 400) {
      setAsientoId(movcabId);
    }
    lastClick.current = { id: movcabId, ts: now };
  }

  // ── Período resumen como label ─────────────────────────────────────────────
  function periodoLabel() {
    if (modoFecha) {
      const d = fechaDesde ? fmtFecha(fechaDesde) : '—';
      const h = fechaHasta ? fmtFecha(fechaHasta) : '—';
      return `${d} → ${h}`;
    }
    return `${fmtMes(periodoDesde)} → ${fmtMes(periodoHasta)}`;
  }

  // ── Excel ─────────────────────────────────────────────────────────────────
  async function exportarExcel() {
    if (!data) return;
    const conAux = cuentaUsaAuxiliar;
    const codigo = data.cuenta?.codigo || cuentaSel?.codigo || '';
    const descripcion = data.cuenta?.nombre || cuentaSel?.descripcion || '';

    const columns = [
      { header: 'Período', key: '_periodo', width: 12 },
      { header: 'Fecha', key: '_fecha', width: 12 },
      { header: 'N° Asiento', key: '_numero', width: 14 },
      { header: 'Tipo', key: '_tipo', width: 12 },
      { header: 'Glosa', key: '_glosa', width: 36 },
      ...(conAux ? [
        { header: 'RUC', key: '_ruc', width: 13 },
        { header: 'Razón Social', key: '_razon', width: 28 },
      ] : []),
      { header: 'Debe', key: '_debe', width: 14, type: 'number', totKey: 'debe' },
      { header: 'Haber', key: '_haber', width: 14, type: 'number', totKey: 'haber' },
      { header: 'Saldo Acumulado', key: '_saldo', width: 16, type: 'number' },
    ];
    const rows = data.movimientos.map(m => ({
      _periodo: fmtMes(m.periodo), _fecha: fmtFecha(m.fecha), _numero: m.numero, _tipo: m.tipo_asiento || '',
      _glosa: m.detalle_glosa || m.glosa,
      ...(conAux ? { _ruc: m.auxiliar_ruc || '', _razon: m.auxiliar_razon_social || '' } : {}),
      _debe: m.debe, _haber: m.haber, _saldo: m.saldo_acum,
    }));
    const totals = { debe: data.total_debe, haber: data.total_haber };

    const sheets = [{
      empresa, titulo: `ANÁLISIS DE CUENTA — ${codigo} ${descripcion}`, periodoLabel: periodoLabel(),
      columns, rows, totals, sheetName: 'Movimientos', landscape: true,
    }];

    // Hoja resumen por auxiliar, en el mismo archivo (segunda hoja) — dos descargas
    // separadas del navegador bloqueaba silenciosamente la segunda.
    if (conAux && data.resumen_por_auxiliar?.length > 0) {
      const columnsRes = [
        { header: 'RUC', key: '_ruc', width: 13 },
        { header: 'Razón Social', key: '_razon', width: 32 },
        { header: 'Total Debe', key: '_debe', width: 14, type: 'number', totKey: 'debe' },
        { header: 'Total Haber', key: '_haber', width: 14, type: 'number', totKey: 'haber' },
        { header: 'Saldo', key: '_saldo', width: 14, type: 'number', totKey: 'saldo' },
      ];
      const rowsRes = data.resumen_por_auxiliar.map(a => ({
        _ruc: a.auxiliar_ruc || '', _razon: a.auxiliar_razon_social,
        _debe: a.debe, _haber: a.haber, _saldo: a.saldo,
      }));
      const totalsRes = {
        debe: data.resumen_por_auxiliar.reduce((s, a) => s + (a.debe || 0), 0),
        haber: data.resumen_por_auxiliar.reduce((s, a) => s + (a.haber || 0), 0),
        saldo: data.resumen_por_auxiliar.reduce((s, a) => s + (a.saldo || 0), 0),
      };
      sheets.push({
        empresa, titulo: `ANÁLISIS DE CUENTA — RESUMEN AUXILIAR — ${codigo} ${descripcion}`, periodoLabel: periodoLabel(),
        columns: columnsRes, rows: rowsRes, totals: totalsRes, sheetName: 'Resumen Auxiliar', landscape: false,
      });
    }

    await exportMultiSheetXlsx({
      sheets,
      filename: `AnalisisCuenta_${codigo}_${periodoLabel().replace(/[→\s]/g,'_')}.xlsx`,
    });
  }

  // ── PDF ───────────────────────────────────────────────────────────────────
  function exportarPdf() {
    if (!data) return;
    const conAux = cuentaUsaAuxiliar;
    const doc = new jsPDF({ orientation: 'landscape' });

    doc.setFontSize(13);
    doc.text(`Análisis de Cuenta: ${data.cuenta?.codigo} — ${data.cuenta?.nombre}`, 14, 15);
    doc.setFontSize(9);
    doc.text(
      `${empresa?.razon_social || ''} | Período: ${periodoLabel()}` +
      (auxSel ? ` | Auxiliar: ${auxSel.numero_documento} ${auxSel.razon_social}` : ''),
      14, 22
    );

    const head = [['Período','Fecha','N° Asiento','Tipo','Glosa',
      ...(conAux ? ['RUC','Razón Social'] : []),
      'Debe','Haber','Saldo']];

    const body = [
      ...data.movimientos.map(m => [
        fmtMes(m.periodo), fmtFecha(m.fecha), m.numero, m.tipo_asiento || '',
        (m.detalle_glosa || m.glosa || '').substring(0, 45),
        ...(conAux ? [(m.auxiliar_ruc || '—'), (m.auxiliar_razon_social || '—').substring(0,25)] : []),
        fmt(m.debe), fmt(m.haber), fmt(m.saldo_acum),
      ]),
      ['','','','','TOTALES',
        ...(conAux ? ['',''] : []),
        fmt(data.total_debe), fmt(data.total_haber), fmt(data.saldo_final),
      ],
    ];

    const glosaIdx = 4;
    autoTable(doc, {
      startY: 28,
      head,
      body,
      styles: { fontSize: 6.5 },
      headStyles: { fillColor: [25, 118, 210] },
      columnStyles: {
        [glosaIdx]: { cellWidth: conAux ? 40 : 65 },
        ...(conAux ? { [glosaIdx+1]: { cellWidth: 16 }, [glosaIdx+2]: { cellWidth: 28 } } : {}),
        [glosaIdx + (conAux ? 3 : 1)]: { halign: 'right' },
        [glosaIdx + (conAux ? 4 : 2)]: { halign: 'right' },
        [glosaIdx + (conAux ? 5 : 3)]: { halign: 'right' },
      },
    });

    // Resumen por auxiliar al pie del PDF
    if (conAux && data.resumen_por_auxiliar?.length > 0) {
      const finalY = doc.lastAutoTable.finalY + 10;
      doc.setFontSize(10);
      doc.text('Resumen por Auxiliar', 14, finalY);
      autoTable(doc, {
        startY: finalY + 4,
        head: [['RUC','Razón Social','Total Debe','Total Haber','Saldo']],
        body: data.resumen_por_auxiliar.map(a => [
          a.auxiliar_ruc || '—', a.auxiliar_razon_social,
          fmt(a.debe), fmt(a.haber), fmt(a.saldo),
        ]),
        styles: { fontSize: 7 },
        headStyles: { fillColor: [46, 125, 50] },
        columnStyles: { 2: { halign: 'right' }, 3: { halign: 'right' }, 4: { halign: 'right' } },
      });
    }

    const codigo = data.cuenta?.codigo || 'cuenta';
    doc.save(`AnalisisCuenta_${codigo}_${periodoLabel().replace(/[→\s]/g,'_')}.pdf`);
  }

  const saldoFinal = data?.saldo_final || 0;
  const hasMov     = data?.movimientos?.length > 0;

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ width: '100%', maxWidth: 1500, mx: 'auto', mt: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <ManageSearchIcon color="primary" />
        <Box>
          <Typography variant="h5" fontWeight="bold">Análisis de Cuenta</Typography>
          <Typography variant="body2" color="text.secondary">
            Movimientos detallados con saldo acumulado · Doble clic en una fila para ver el asiento completo
          </Typography>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* ── Panel de filtros ── */}
      <Paper sx={{ p: 2, mb: 3, borderRadius: 3 }}>
        <Grid container spacing={2} alignItems="flex-end">

          {/* Empresa */}
          <Grid item xs={12} sm="auto">
            <TextField select fullWidth size="small" label="Empresa" value={empresaId}
              onChange={e => { setEmpresaId(e.target.value); setData(null); }}
              sx={{ minWidth: '22em' }}>
              {empresas.map(e => <MenuItem key={e.id} value={e.id}>{e.razon_social}</MenuItem>)}
            </TextField>
          </Grid>

          {/* Cuenta */}
          <Grid item xs={12} sm="auto" sx={{ minWidth: '24em' }}>
            <Autocomplete
              options={cuentaOpts}
              getOptionLabel={o => `${o.codigo} — ${o.descripcion}`}
              value={cuentaSel}
              onChange={(_, v) => { setCuentaSel(v); setData(null); setAuxSel(null); }}
              inputValue={inputCuenta}
              onInputChange={(_, v) => setInputCuenta(v)}
              loading={buscandoCuenta}
              disabled={!empresaId}
              size="small"
              noOptionsText="Escribe para buscar..."
              renderInput={params => (
                <TextField {...params} label="Cuenta contable" fullWidth
                  InputProps={{ ...params.InputProps, endAdornment: (
                    <>{buscandoCuenta ? <CircularProgress size={16}/> : null}{params.InputProps.endAdornment}</>
                  )}} />
              )}
              renderOption={(props, o) => (
                <Box component="li" {...props} key={o.id}>
                  <Typography sx={{ fontFamily: 'monospace', color: '#1976d2', mr: 1, fontSize: '0.85rem' }}>{o.codigo}</Typography>
                  <Typography variant="body2">{o.descripcion}</Typography>
                </Box>
              )}
            />
          </Grid>

          {/* Auxiliar (RUC/Razón Social) */}
          <Grid item xs={12} sm="auto" sx={{ minWidth: '22em' }}>
            <Autocomplete
              options={auxOpts}
              getOptionLabel={o => `${o.numero_documento || ''} — ${o.razon_social || ''}`}
              value={auxSel}
              onChange={(_, v) => { setAuxSel(v); setData(null); }}
              inputValue={inputAux}
              onInputChange={(_, v) => setInputAux(v)}
              loading={buscandoAux}
              disabled={!empresaId}
              size="small"
              clearOnEscape
              noOptionsText={cuentaSel ? 'Sin auxiliares para esta cuenta' : 'Escribe RUC o razón social...'}
              renderInput={params => (
                <TextField {...params} label="Auxiliar (RUC / Razón Social)"
                  placeholder="Todos"
                  InputProps={{ ...params.InputProps,
                    startAdornment: <PersonSearchIcon sx={{ color: 'action.active', mr: 0.5, fontSize: 18 }} />,
                    endAdornment: (
                      <>{buscandoAux ? <CircularProgress size={16}/> : null}{params.InputProps.endAdornment}</>
                    )
                  }} />
              )}
              renderOption={(props, o) => (
                <Box component="li" {...props} key={o.id}>
                  <Typography sx={{ fontFamily: 'monospace', color: '#555', mr: 1, fontSize: '0.82rem', minWidth: 110 }}>
                    {o.numero_documento || '—'}
                  </Typography>
                  <Box>
                    <Typography variant="body2" fontWeight="bold">{o.razon_social}</Typography>
                    <Typography variant="caption" color="text.secondary">{o.tipo}</Typography>
                  </Box>
                </Box>
              )}
            />
          </Grid>

          {/* Salto de línea — segunda fila de filtros */}
          <Grid item xs={12} sx={{ pt: '4px !important', pb: 0 }} />

          {/* Toggle modo fecha */}
          <Grid item xs={12} sm="auto">
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Chip
                label="Por período"
                size="small"
                color={!modoFecha ? 'primary' : 'default'}
                onClick={() => setModoFecha(false)}
                variant={!modoFecha ? 'filled' : 'outlined'}
                sx={{ cursor: 'pointer' }}
              />
              <Chip
                label="Por fecha"
                size="small"
                color={modoFecha ? 'secondary' : 'default'}
                onClick={() => setModoFecha(true)}
                variant={modoFecha ? 'filled' : 'outlined'}
                sx={{ cursor: 'pointer' }}
              />
            </Box>
          </Grid>

          {/* Filtros de período */}
          {!modoFecha ? (
            <>
              <Grid item xs={12} sm="auto">
                <TextField select fullWidth size="small" label="Período Desde" value={periodoDesde}
                  onChange={e => setPeriodoDesde(e.target.value)} disabled={!empresaId}
                  sx={{ minWidth: '11em' }}>
                  {periodos.map(p => <MenuItem key={p.periodo} value={p.periodo}>{fmtMes(p.periodo)}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={12} sm="auto">
                <TextField select fullWidth size="small" label="Período Hasta" value={periodoHasta}
                  onChange={e => setPeriodoHasta(e.target.value)} disabled={!empresaId}
                  sx={{ minWidth: '11em' }}>
                  {periodos.map(p => <MenuItem key={p.periodo} value={p.periodo}>{fmtMes(p.periodo)}</MenuItem>)}
                </TextField>
              </Grid>
            </>
          ) : (
            <>
              <Grid item xs={12} sm="auto">
                <TextField size="small" label="Fecha Desde" type="date" value={fechaDesde}
                  onChange={e => setFechaDesde(e.target.value)} disabled={!empresaId}
                  InputLabelProps={{ shrink: true }} sx={{ minWidth: '11em' }} />
              </Grid>
              <Grid item xs={12} sm="auto">
                <TextField size="small" label="Fecha Hasta" type="date" value={fechaHasta}
                  onChange={e => setFechaHasta(e.target.value)} disabled={!empresaId}
                  InputLabelProps={{ shrink: true }} sx={{ minWidth: '11em' }} />
              </Grid>
            </>
          )}

          {/* Botón analizar */}
          <Grid item xs={12} sm="auto">
            <Button variant="contained" startIcon={loading ? <CircularProgress size={16} color="inherit"/> : <SearchIcon/>}
              onClick={analizar} disabled={loading || !empresaId || !cuentaSel}>
              Analizar
            </Button>
          </Grid>

          {/* Exportar */}
          {data && <>
            <Grid item xs={12} sm="auto">
              <Button variant="outlined" startIcon={<DownloadIcon/>} onClick={exportarExcel} color="success" size="small">
                Excel
              </Button>
            </Grid>
            <Grid item xs={12} sm="auto">
              <Button variant="outlined" startIcon={<PictureAsPdfIcon/>} onClick={exportarPdf} color="error" size="small">
                PDF
              </Button>
            </Grid>
          </>}
        </Grid>
      </Paper>

      {loading && <CircularProgress sx={{ display: 'block', mx: 'auto', my: 4 }}/>}

      {!loading && data && (
        <>
          {/* ── Cabecera de resultado ── */}
          <Paper sx={{ p: 2.5, mb: 2, borderRadius: 3, borderLeft: '5px solid #1976d2' }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
              <Box>
                <Typography variant="h6" fontWeight="bold">
                  {data.cuenta?.codigo} — {data.cuenta?.nombre}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {empresa?.razon_social} &nbsp;·&nbsp; {periodoLabel()}
                  {auxSel && <> &nbsp;·&nbsp; <b>{auxSel.numero_documento}</b> {auxSel.razon_social}</>}
                </Typography>
              </Box>
              <Tooltip title="Doble clic en cualquier fila de movimientos para ver el asiento contable completo">
                <Chip icon={<ReceiptLongIcon/>} label="Doble clic → ver asiento" size="small"
                  variant="outlined" color="primary" sx={{ cursor: 'default' }}/>
              </Tooltip>
            </Box>

            <Grid container spacing={2} mt={0.5}>
              {[
                { label: 'Total Debe',  value: data.total_debe,  color: 'primary.main' },
                { label: 'Total Haber', value: data.total_haber, color: 'secondary.main' },
                { label: `Saldo Final (${saldoFinal >= 0 ? 'D' : 'A'})`,
                  value: Math.abs(saldoFinal),
                  color: saldoFinal >= 0 ? 'success.main' : 'error.main',
                  bg: saldoFinal >= 0 ? '#e8f5e9' : '#ffebee' },
              ].map(({ label, value, color, bg }) => (
                <Grid item xs={12} sm={4} key={label}>
                  <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center', borderRadius: 2, bgcolor: bg }}>
                    <Typography variant="caption" color="text.secondary">{label}</Typography>
                    <Typography variant="h6" fontWeight="bold" sx={{ color }}>S/ {fmt(value)}</Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Paper>

          {/* ── Resumen por auxiliar ── */}
          {cuentaUsaAuxiliar && !auxSel && data.resumen_por_auxiliar?.length > 0 && (
            <Paper sx={{ p: 2, mb: 2, borderRadius: 3, border: '1px solid #c8e6c9' }}>
              <Typography fontWeight="bold" fontSize={13} mb={1} color="#2e7d32">
                Resumen por Auxiliar ({data.resumen_por_auxiliar.length})
              </Typography>
              <Box sx={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#e8f5e9' }}>
                      {['RUC','Razón Social','Debe','Haber','Saldo'].map(h => (
                        <TableCell key={h} sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}
                          align={['Debe','Haber','Saldo'].includes(h) ? 'right' : 'left'}>{h}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.resumen_por_auxiliar.map((a, i) => (
                      <TableRow key={i} hover
                        onClick={() => { setAuxSel(a.auxiliar_id ? { id: a.auxiliar_id, numero_documento: a.auxiliar_ruc, razon_social: a.auxiliar_razon_social } : null); }}
                        sx={{ cursor: a.auxiliar_id ? 'pointer' : 'default', '&:hover': { bgcolor: '#f1f8e9' } }}>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.78rem', color: '#555' }}>
                          {a.auxiliar_ruc || '—'}
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.82rem' }}>{a.auxiliar_razon_social}</TableCell>
                        <TableCell align="right" sx={{ color: '#1565c0', fontSize: '0.82rem' }}>{fmt(a.debe)}</TableCell>
                        <TableCell align="right" sx={{ color: '#c62828', fontSize: '0.82rem' }}>{fmt(a.haber)}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '0.82rem',
                          color: a.saldo >= 0 ? 'success.main' : 'error.main' }}>
                          {fmt(Math.abs(a.saldo))} {a.saldo >= 0 ? 'D' : 'A'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
              <Typography variant="caption" color="text.secondary" mt={0.5} display="block">
                Clic en una fila para filtrar por ese auxiliar
              </Typography>
            </Paper>
          )}

          {/* ── Grilla de movimientos ── */}
          {!hasMov ? (
            <Alert severity="info">No hay movimientos para los filtros seleccionados.</Alert>
          ) : (
            <TableContainer component={Paper} sx={{ borderRadius: 3, overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: cuentaUsaAuxiliar ? 1150 : 900 }}>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'primary.main' }}>
                    {[
                      { label: 'Período',    w: 90  },
                      { label: 'Fecha',      w: 95  },
                      { label: 'N° Asiento', w: 115 },
                      { label: 'Tipo',       w: 80  },
                      ...(cuentaUsaAuxiliar ? [
                        { label: 'RUC',         w: 105 },
                        { label: 'Razón Social', w: 160 },
                      ] : []),
                      { label: 'Glosa' },
                      { label: 'Debe',        w: 115, align: 'right' },
                      { label: 'Haber',       w: 115, align: 'right' },
                      { label: 'Saldo Acum.', w: 125, align: 'right' },
                    ].map(({ label, w, align }) => (
                      <TableCell key={label}
                        sx={{ color: 'white', fontWeight: 'bold', width: w, py: 1 }}
                        align={align || 'left'}>{label}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.movimientos.map((m, i) => (
                    <TableRow key={i}
                      onClick={e => handleRowClick(e, m.movcab_id)}
                      hover
                      sx={{
                        cursor: 'pointer',
                        '&:nth-of-type(odd)': { bgcolor: '#fafafa' },
                        '&:hover': { bgcolor: '#e8f0fe !important' },
                        userSelect: 'none',
                      }}>
                      <TableCell>
                        <Typography variant="caption" fontWeight="bold" color="primary.main">
                          {fmtMes(m.periodo)}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.8rem' }}>{fmtFecha(m.fecha)}</TableCell>
                      <TableCell>
                        <Tooltip title="Doble clic para ver asiento completo">
                          <Chip label={m.numero} size="small" variant="outlined" color="primary"
                            sx={{ fontFamily: 'monospace', fontSize: '0.73rem', cursor: 'pointer' }}/>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        {m.tipo_asiento && (
                          <Chip label={m.tipo_asiento} size="small"
                            sx={{ bgcolor: tipoChipColor(m.tipo_asiento), color: '#fff',
                                  fontSize: '0.68rem', fontWeight: 'bold', height: 20 }}/>
                        )}
                      </TableCell>
                      {cuentaUsaAuxiliar && (
                        <>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.78rem', color: '#555' }}>
                            {m.auxiliar_ruc || <span style={{ color: '#bbb' }}>—</span>}
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.8rem' }}>
                            {m.auxiliar_razon_social
                              ? <Typography fontSize="0.8rem" fontWeight={500}>{m.auxiliar_razon_social}</Typography>
                              : <span style={{ color: '#bbb' }}>—</span>}
                          </TableCell>
                        </>
                      )}
                      <TableCell sx={{ fontSize: '0.82rem', maxWidth: 300 }}>
                        {m.detalle_glosa || m.glosa}
                      </TableCell>
                      <TableCell align="right"
                        sx={{ color: m.debe > 0 ? '#1565c0' : 'text.disabled',
                              fontWeight: m.debe > 0 ? 'bold' : 'normal', fontSize: '0.82rem' }}>
                        {m.debe > 0 ? fmt(m.debe) : '—'}
                      </TableCell>
                      <TableCell align="right"
                        sx={{ color: m.haber > 0 ? '#c62828' : 'text.disabled',
                              fontWeight: m.haber > 0 ? 'bold' : 'normal', fontSize: '0.82rem' }}>
                        {m.haber > 0 ? fmt(m.haber) : '—'}
                      </TableCell>
                      <TableCell align="right">
                        <Typography fontSize="0.83rem" fontWeight="bold"
                          sx={{ color: m.saldo_acum >= 0 ? 'success.main' : 'error.main' }}>
                          {fmt(Math.abs(m.saldo_acum))} {m.saldo_acum >= 0 ? 'D' : 'A'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}

                  {/* Fila de totales */}
                  <TableRow sx={{ bgcolor: '#e3f2fd' }}>
                    <TableCell colSpan={4 + (cuentaUsaAuxiliar ? 2 : 0)}>
                      <Typography fontWeight="bold" fontSize={13}>TOTALES</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography fontWeight="bold" color="primary.main">S/ {fmt(data.total_debe)}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography fontWeight="bold" color="error.main">S/ {fmt(data.total_haber)}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography fontWeight="bold"
                        sx={{ color: saldoFinal >= 0 ? 'success.main' : 'error.main' }}>
                        S/ {fmt(Math.abs(saldoFinal))} {saldoFinal >= 0 ? 'D' : 'A'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          )}

          <Typography variant="caption" color="text.secondary" mt={1} display="block" textAlign="right">
            {data.movimientos.length} movimiento{data.movimientos.length !== 1 ? 's' : ''}
          </Typography>
        </>
      )}

      {!empresaId && (
        <Paper sx={{ p: 6, borderRadius: 3, textAlign: 'center', bgcolor: '#f8f9fa' }}>
          <ManageSearchIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }}/>
          <Typography variant="h6" color="text.secondary">
            Selecciona empresa y cuenta para ver sus movimientos
          </Typography>
          <Typography variant="body2" color="text.disabled" mt={1}>
            Usa el filtro de auxiliar para ver movimientos por cliente o proveedor específico
          </Typography>
        </Paper>
      )}

      {/* ── Popup asiento ── */}
      <AsientoDialog movcabId={asientoId} onClose={() => setAsientoId(null)}/>
    </Box>
  );
}
